import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import { RootState } from "..";
import { authApi } from "../services/authService";
import { removeAuth } from "../slices/authSlice";

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: "https://graph.microsoft.com/v1.0",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.azure_token;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const state = api.getState() as RootState;

        const refreshToken = state.auth.azure_refresh_token;
        if (!refreshToken) {
          api.dispatch(removeAuth());
          throw new Error("No refresh token available");
        }
        const refreshResult = await api.dispatch(
          authApi.endpoints.refreshAzureToken.initiate({
            refresh_token: refreshToken,
          })
        );
        console.log("refreshResult", refreshResult);
        if (refreshResult.data && refreshResult.data.access_token) {
          const { updateAzureTokens } = await import("../slices/authSlice");
          await api.dispatch(
            updateAzureTokens({
              azure_refresh_token: refreshResult.data.refresh_token,
              azure_token: refreshResult.data.access_token,
              azure_token_expires:
                Date.now() + refreshResult.data.expires_in * 1000,
            })
          );
          // retry the initial query

          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(removeAuth());
          // if we get here, it means the refresh token is invalid
        }
      } finally {
        // release must be called once the mutex should be released again.

        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();

      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};
// initialize an empty api service that we'll inject endpoints into later as needed
const msBaseApi = createApi({
  reducerPath: "msBaseApi",
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: [
    "User",
    "Calendar",
    "Email",
    "OneDrive",
    "Teams",
    "CompleteMeetingHistory",
    "PastMeetings",
    "AttendanceReport"
  ],
});

export default msBaseApi;
